import { Fragment, useState, useEffect } from "react";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import toast from "react-hot-toast";
import moment from "moment";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import CategoryIcon from "@mui/icons-material/Category";
import * as XLSX from "xlsx";

const RelatorioProcedimentosExecutados = ({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
}) => {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();
  const [dadosRelatorio, setDadosRelatorio] = useState(null);

  useEffect(() => {
    if (
      acao &&
      dataFiltro.dataInicial &&
      dataFiltro.dataFinal &&
      atualizacoes > 0
    ) {
      buscarDadosRelatorio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acao, dataFiltro, atualizacoes]);

  const buscarDadosRelatorio = async () => {
    startLoading();
    try {
      const dataInicio = moment(dataFiltro.dataInicial).format("YYYY-MM-DD");
      const dataFim = moment(dataFiltro.dataFinal).format("YYYY-MM-DD");

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/getProcedimentosExecutados/${dadosClinica._id}`,
        "POST",
        JSON.stringify({
          dataInicio,
          dataFim,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      setDadosRelatorio(responseData);

      if (acao === "exportar") {
        exportarParaExcel(responseData);
      }

      toast.success("Relatório gerado com sucesso!");
    } catch (err) {
      console.error("Erro ao buscar dados do relatório:", err);
      toast.error(
        "Erro ao gerar relatório: " + (err.message || "Erro desconhecido"),
      );
    } finally {
      stopLoading();
    }
  };

  const formatarNumero = (valor) =>
    new Intl.NumberFormat("pt-PT").format(valor || 0);

  // Período: usa o devolvido pelo backend, com fallback para o filtro selecionado
  const periodoInicio =
    dadosRelatorio?.periodo?.inicio || dataFiltro.dataInicial;
  const periodoFim = dadosRelatorio?.periodo?.fim || dataFiltro.dataFinal;

  const exportarParaExcel = (dados) => {
    try {
      const workbook = XLSX.utils.book_new();
      const linhas = [];

      linhas.push([`Procedimentos Executados — ${dadosClinica.nome}`]);
      linhas.push([
        `Período: ${moment(
          dados.periodo?.inicio || dataFiltro.dataInicial,
        ).format("DD/MM/YYYY")} a ${moment(
          dados.periodo?.fim || dataFiltro.dataFinal,
        ).format("DD/MM/YYYY")}`,
      ]);
      linhas.push([]);

      linhas.push(["Categoria", "Em Plano", "Isolados", "Total"]);

      (dados.categorias || []).forEach((c) => {
        linhas.push([c.categoria, c.emPlano, c.isolados, c.total]);
        (c.tratamentos || []).forEach((t) => {
          linhas.push([`    ${t.tratamento}`, t.emPlano, t.isolados, t.total]);
        });
      });

      linhas.push([]);
      linhas.push([
        "TOTAL",
        dados.totais.emPlano,
        dados.totais.isolados,
        dados.totais.total,
      ]);

      const ws = XLSX.utils.aoa_to_sheet(linhas);
      ws["!cols"] = [{ wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, ws, "Procedimentos");

      const fileName = `Procedimentos_Executados_${moment(
        dados.periodo?.inicio || dataFiltro.dataInicial,
      ).format("DD-MM-YYYY")}_${moment(
        dados.periodo?.fim || dataFiltro.dataFinal,
      ).format("DD-MM-YYYY")}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success("Arquivo Excel exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar para Excel:", error);
      toast.error("Erro ao exportar arquivo Excel");
    }
  };

  if (!dadosRelatorio) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h6" color="textSecondary">
          Selecione um período e clique em "Gerar Relatório" para visualizar os
          dados.
        </Typography>
      </Box>
    );
  }

  const totalGeral = dadosRelatorio.totais?.total || 0;

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header do Relatório */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AssignmentTurnedInIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="h5" component="h2">
              Procedimentos Executados
            </Typography>
          </Box>
          <Typography variant="subtitle1" color="textSecondary">
            Clínica: {dadosClinica.nome} | Período:{" "}
            {moment(periodoInicio).format("DD/MM/YYYY")} a{" "}
            {moment(periodoFim).format("DD/MM/YYYY")}
          </Typography>
        </CardContent>
      </Card>

      {/* Cards de totais globais */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: "4px solid #1976d2" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AssignmentTurnedInIcon sx={{ mr: 1, color: "primary.main" }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {formatarNumero(dadosRelatorio.totais.total)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total de Procedimentos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: "4px solid #4caf50" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LocalHospitalIcon sx={{ mr: 1, color: "success.main" }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {formatarNumero(dadosRelatorio.totais.emPlano)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Em Plano de Tratamento
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: "4px solid #9c27b0" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <MedicalServicesIcon sx={{ mr: 1, color: "secondary.main" }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {formatarNumero(dadosRelatorio.totais.isolados)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Tratamentos Isolados
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela por categoria */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Por Categoria ({dadosRelatorio.categorias.length})
      </Typography>

      {dadosRelatorio.categorias.length === 0 ? (
        <Card>
          <CardContent>
            <Typography
              variant="body1"
              color="textSecondary"
              textAlign="center"
            >
              Nenhum procedimento executado no período selecionado.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell>
                  <strong>Categoria</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Em Plano</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Isolados</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Total</strong>
                </TableCell>
                <TableCell sx={{ width: "25%" }}>
                  <strong>Peso</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dadosRelatorio.categorias.map((c) => {
                const pesoCat =
                  totalGeral > 0 ? (c.total / totalGeral) * 100 : 0;
                return (
                  <Fragment key={c.categoriaId || c.categoria}>
                    {/* Linha da categoria (subtotal) */}
                    <TableRow sx={{ bgcolor: "grey.100" }}>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CategoryIcon
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                          <Typography variant="body2" fontWeight="bold">
                            {c.categoria}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatarNumero(c.emPlano)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatarNumero(c.isolados)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="primary.main">
                          {formatarNumero(c.total)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={pesoCat}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Chip
                            label={`${pesoCat.toFixed(1)}%`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Linhas dos tratamentos da categoria */}
                    {(c.tratamentos || []).map((t) => (
                      <TableRow key={t.tratamentoId || t.tratamento} hover>
                        <TableCell sx={{ pl: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t.tratamento}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatarNumero(t.emPlano)}
                        </TableCell>
                        <TableCell align="right">
                          {formatarNumero(t.isolados)}
                        </TableCell>
                        <TableCell align="right">
                          {formatarNumero(t.total)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    ))}
                  </Fragment>
                );
              })}
              {/* Linha de totais */}
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell>
                  <strong>TOTAL</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>
                    {formatarNumero(dadosRelatorio.totais.emPlano)}
                  </strong>
                </TableCell>
                <TableCell align="right">
                  <strong>
                    {formatarNumero(dadosRelatorio.totais.isolados)}
                  </strong>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold" color="primary.main">
                    {formatarNumero(dadosRelatorio.totais.total)}
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RelatorioProcedimentosExecutados;
